import React, { useEffect, useState, memo } from 'react';
import { scrollToPurchase } from '../utils/checkout';
export function FinalCTA() {
  const [timeLeft, setTimeLeft] = useState(() => {
    // Check for saved end time in sessionStorage
    const saved = sessionStorage.getItem('launch_end');
    if (saved) {
      const remaining = Math.max(0, Math.floor((parseInt(saved) - Date.now()) / 1000));
      return remaining;
    }
    // 18 hours, 33 minutes, 9 seconds
    const total = 18 * 3600 + 33 * 60 + 9;
    sessionStorage.setItem('launch_end', String(Date.now() + total * 1000));
    return total;
  });
  useEffect(() => {
    if (timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor(timeLeft % 3600 / 60);
  const seconds = timeLeft % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  const comparisonRows = [{
    left: 'Taking lots of photos and loving very few',
    right: 'A gallery full of photos you never use'
  }, {
    left: 'Missing beautiful moments because you were guessing',
    right: 'Moments that deserved better'
  }, {
    left: 'Relying on luck instead of knowing what works',
    right: 'More frustration every time you open your camera'
  }, {
    left: 'Feeling disappointed by dark, blurry, or messy shots',
    right: 'Beautiful memories that do not feel as special on screen'
  }, {
    left: 'Letting meaningful memories pass without capturing them well',
    right: 'The same cycle, next month, next season, next year'
  }];
  return <section className="bg-cream py-20 md:py-28 px-4" data-id="element-106">
      <div className="max-w-4xl mx-auto" data-id="element-107">
        {/* Heading */}
        <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-center text-warm-hero mb-12 leading-tight" data-id="element-108">
          What does it cost to keep guessing?
        </h2>

        {/* Comparison Table */}
        <div className="bg-white rounded-2xl border border-soft-gold/20 shadow-lg overflow-hidden mb-12" data-id="element-109">
          {/* Headers */}
          <div className="hidden md:grid grid-cols-2 bg-warm-light border-b border-soft-gold/20 p-6" data-id="element-110">
            <h3 className="font-sans font-bold text-lg text-warm-hero" data-id="element-111">
              Without The Pawfect Shot MegaPack
            </h3>
            <h3 className="font-sans font-bold text-lg text-orange-dark" data-id="element-112">
              What it really costs you
            </h3>
          </div>

          {/* Rows */}
          <div className="divide-y divide-soft-gold/15" data-id="element-113">
            {comparisonRows.map((row, index) => <div key={index} className="grid grid-cols-1 md:grid-cols-2 p-5 md:p-6 gap-2 md:gap-6 hover:bg-warm-light/30 transition-colors" data-id="element-114">
                {/* Mobile Header Left */}
                <div className="md:hidden font-sans font-bold text-sm text-warm-hero/70 uppercase tracking-wider mb-1" data-id="element-115">
                  Without MegaPack
                </div>
                <div className="font-sans text-base md:text-lg text-warm-hero" data-id="element-116">
                  {row.left}
                </div>

                {/* Mobile Header Right */}
                <div className="md:hidden font-sans font-bold text-sm text-orange-dark/70 uppercase tracking-wider mt-3 mb-1" data-id="element-117">
                  What it costs you
                </div>
                <div className="font-sans text-base md:text-lg font-bold text-orange-dark" data-id="element-118">
                  {row.right}
                </div>
              </div>)}
          </div>
        </div>

        {/* Emotional Closing Line */}
        <p className="font-serif italic text-xl md:text-2xl text-center text-warm-hero mb-16" data-id="element-119">
          Your next favorite photo of your dog could be one setup away.
        </p>

        {/* Countdown Timer Block */}
        <div className="text-center bg-white rounded-2xl border border-soft-gold/20 shadow-md p-8 md:p-10 max-w-2xl mx-auto" data-id="element-120">
          <h3 className="font-sans font-bold text-lg md:text-xl text-warm-hero uppercase tracking-wider mb-6" data-id="element-121">
            ⏰ Launch price ends in:
          </h3>

          <div className="flex items-center justify-center gap-3 md:gap-6 mb-6" data-id="element-122">
            <div className="flex flex-col items-center" data-id="element-123">
              <span className="font-serif text-5xl md:text-7xl font-bold text-soft-gold leading-none tracking-tight" data-id="element-124">
                {pad(hours)}
              </span>
              <span className="font-sans text-xs md:text-sm font-bold text-warm-hero/60 uppercase tracking-widest mt-2" data-id="element-125">
                HRS
              </span>
            </div>
            <span className="font-serif text-4xl md:text-6xl font-bold text-soft-gold/40 -mt-4" data-id="element-126">
              :
            </span>
            <div className="flex flex-col items-center" data-id="element-127">
              <span className="font-serif text-5xl md:text-7xl font-bold text-soft-gold leading-none tracking-tight" data-id="element-128">
                {pad(minutes)}
              </span>
              <span className="font-sans text-xs md:text-sm font-bold text-warm-hero/60 uppercase tracking-widest mt-2" data-id="element-129">
                MIN
              </span>
            </div>
            <span className="font-serif text-4xl md:text-6xl font-bold text-soft-gold/40 -mt-4" data-id="element-130">
              :
            </span>
            <div className="flex flex-col items-center" data-id="element-131">
              <span className="font-serif text-5xl md:text-7xl font-bold text-soft-gold leading-none tracking-tight" data-id="element-132">
                {pad(seconds)}
              </span>
              <span className="font-sans text-xs md:text-sm font-bold text-warm-hero/60 uppercase tracking-widest mt-2" data-id="element-133">
                SEC
              </span>
            </div>
          </div>

          <p className="font-sans text-base md:text-lg font-medium text-warm-hero max-w-md mx-auto" data-id="element-134">
            After that, the price goes up. This launch price will not stay
            available.
          </p>
        </div>
      </div>
    </section>;
}